import { useEffect } from 'react'
import { emojiFromEmotion } from '../../../../components/DeviceScreenPreview'
import { normalizeEmotion } from '../../../../utils/emotion'
import { safeStorageGet, safeStorageSet } from '../../../../utils/storage'
import { buildWsUrlCandidates, fetchLastDeviceId } from './monitorWs'

export function useMonitorConnection(context) {
  const { addMessage, countdownTimerRef, historyLoadedRef, lastErrorRef,
    manualCloseRef, pingTimerRef, reconnectTimerRef, resetStream, setConnecting,
    setDebugEvents, setDeviceId, setDeviceMeta, setDeviceOnline, setEmotion,
    setEmotionEmoji, setIsTyping, setMessages, setPipeline, setRetryIn,
    setStreamText, setWsConnected, setWsSessionId, setWsUrl, streamTextRef,
    targetDeviceId, tx, wsCandidateIndexRef, wsConnected, wsRef } = context
  const connect = (overrideDeviceId) => {
    const overrideId = typeof overrideDeviceId === 'string' ? overrideDeviceId : ''
    if (wsRef.current) {
      manualCloseRef.current = true
      wsRef.current.close()
      wsRef.current = null
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current)
      countdownTimerRef.current = null
    }
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current)
      pingTimerRef.current = null
    }
    setConnecting(true)
    setRetryIn(0)
    setWsSessionId('')
    historyLoadedRef.current = false
    try {
      const candidates = buildWsUrlCandidates(overrideId || targetDeviceId)
      const idx = wsCandidateIndexRef.current || 0
      const pick = candidates[Math.min(idx, candidates.length - 1)]
      setWsUrl(pick)
      const ws = new WebSocket(pick)
      wsRef.current = ws

      ws.onopen = () => {
        setWsConnected(true)
        setConnecting(false)
        setRetryIn(0)
        wsCandidateIndexRef.current = 0
        
        ws.send(JSON.stringify({
          type: 'hello',
          version: 1,
          client_id: 'web-listener',
          transport: 'websocket',
          audio_params: {
            format: 'opus',
            sample_rate: 16000,
            channels: 1,
            frame_duration: 60
          }
        }))

        try {
          ws.send(JSON.stringify({ type: 'history' }))
        } catch {}

        // keepalive：避免后端配置了空闲超时导致 monitor 被断开
        pingTimerRef.current = setInterval(() => {
          try {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: 'ping' }))
            }
          } catch {}
        }, 15000)
      }

      ws.onclose = () => {
        const wasManualClose = manualCloseRef.current
        manualCloseRef.current = false
        setWsConnected(false)
        setConnecting(false)
        resetStream()
        if (pingTimerRef.current) {
          clearInterval(pingTimerRef.current)
          pingTimerRef.current = null
        }
        if (wasManualClose) {
          return
        }
        // 连接从未成功建立：尝试切换到下一个 WS 入口（用于 admin-dev 可能未暴露 30001 端口的情况）
        try {
          const nextCandidates = buildWsUrlCandidates(overrideId || targetDeviceId)
          if (!wsConnected && wsCandidateIndexRef.current < nextCandidates.length - 1) {
            wsCandidateIndexRef.current += 1
            reconnectTimerRef.current = setTimeout(() => connect(overrideId || targetDeviceId), 300)
            return
          }
        } catch {}
        let remain = 5
        setRetryIn(remain)
        countdownTimerRef.current = setInterval(() => {
          remain -= 1
          setRetryIn(remain)
          if (remain <= 0) {
            clearInterval(countdownTimerRef.current)
            countdownTimerRef.current = null
          }
        }, 1000)
        reconnectTimerRef.current = setTimeout(connect, 5000)
      }

      ws.onerror = (e) => {
        console.error('WebSocket error:', e)
        setConnecting(false)
      }

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data)
          handleMessage(data)
        } catch (err) {
          console.error('Parse error:', err)
        }
      }
    } catch (e) {
      console.error('Connect error:', e)
      setConnecting(false)
      reconnectTimerRef.current = setTimeout(connect, 5000)
    }
  }

  const handleMessage = (data) => {
    const type = data.type
    const receivedAt = Date.now()

    setDebugEvents((prev) => {
      const brief = (() => {
        try {
          const s = JSON.stringify(data)
          return s.length > 800 ? `${s.slice(0, 800)}…` : s
        } catch {
          return String(data)
        }
      })()
      return [...prev.slice(-79), { at: receivedAt, type: String(type || 'unknown'), brief }]
    })

    if (type === 'history') {
      if (historyLoadedRef.current) return
      historyLoadedRef.current = true

      const items = Array.isArray(data.items) ? data.items : []
      const mapped = items
        .map((it) => {
          const role = String(it?.role || '').trim()
          const content = String(it?.content || '').trim()
          if (!content) return null
          if (role === 'user') return { type: 'user', text: content, timestamp: '', source: 'history' }
          if (role === 'assistant') return { type: 'assistant', text: content, timestamp: '', provider: 'AI', source: 'history' }
          return null
        })
        .filter(Boolean)

      if (mapped.length) {
        setMessages((prev) => {
          const system = prev.filter((m) => m?.type === 'system').slice(-8)
          return [...mapped, ...system].slice(-99)
        })
        addMessage({ type: 'system', text: tx({ zh: '已恢复聊天记录。', en: 'Chat history restored.' }) })
      }
      return
    }

    if (type === 'hello') {
      setWsSessionId((data.session_id || '').substring(0, 8))
      return
    }

    if (type === 'device_info') {
      setDeviceMeta({
        board: data.board || data.device_board || '-',
        version: data.version || data.device_version || '-',
        updatedAt: receivedAt,
      })
      return
    }

    if (type === 'device_status') {
      const online = data.status === 'online'
      setDeviceOnline(online)
      setDeviceId(data.device_id || '-')
      // 不再每次都添加消息，避免刷屏
      return
    }

    if (type === 'set_emotion_ack') {
      const status = String(data.status || '')
      const emo = String(data.emotion || '')
      if (status === 'ok') {
        addMessage({ type: 'system', text: tx({ zh: '成功：表情已下发到设备：{emo}', en: 'OK: emoji sent to device: {emo}' }, { emo }) })
      } else if (status === 'device_offline') {
        addMessage({
          type: 'system',
          text: tx({ zh: '失败：设备离线，无法下发：{emo}', en: 'Failed: device offline, cannot send: {emo}' }, { emo }),
        })
      } else {
        addMessage({
          type: 'system',
          text: tx({ zh: '失败：下发表情失败({status})：{emo}', en: 'Failed to send emoji ({status}): {emo}' }, { status, emo }),
        })
      }
      return
    }

    if (type === 'set_eye_color_ack') {
      const status = String(data.status || '')
      const color = String(data.color || '')
      if (status === 'ok') {
        addMessage({ type: 'system', text: tx({ zh: '成功：眼睛颜色已下发：{color}', en: 'OK: eye color sent: {color}' }, { color }) })
      } else if (status === 'device_offline') {
        addMessage({
          type: 'system',
          text: tx({ zh: '失败：设备离线，无法下发颜色：{color}', en: 'Failed: device offline, cannot send color: {color}' }, { color }),
        })
      } else {
        addMessage({
          type: 'system',
          text: tx({ zh: '失败：下发颜色失败({status})：{color}', en: 'Failed to send color ({status}): {color}' }, { status, color }),
        })
      }
      return
    }

    if (type === 'stories_list') {
      const count = Array.isArray(data.stories) ? data.stories.length : 0
      addMessage({ type: 'system', text: tx({ zh: '故事列表已返回：{count} 条', en: 'Stories list received: {count}' }, { count }) })
      return
    }

    if (type === 'play_story_ack') {
      const status = String(data.status || '')
      const storyId = String(data.story_id || '')
      if (status === 'ok') {
        addMessage({ type: 'system', text: tx({ zh: '故事播放已开始：{id}', en: 'Story playback started: {id}' }, { id: storyId }) })
      } else {
        addMessage({
          type: 'system',
          text: tx({ zh: '故事播放失败({status})：{id}', en: 'Story playback failed ({status}): {id}' }, { status, id: storyId }),
        })
      }
      return
    }

    if (type === 'story_ack') {
      const status = String(data.status || '')
      addMessage({ type: 'system', text: tx({ zh: '故事推送结果：{status}', en: 'Story push result: {status}' }, { status }) })
      return
    }

    if (type === 'animate_ack') {
      const status = String(data.status || '')
      addMessage({ type: 'system', text: tx({ zh: '动画播放结果：{status}', en: 'Animation result: {status}' }, { status }) })
      return
    }

    if (type === 'stt') {
      setPipeline((prev) => ({
        ...prev,
        phase: 'asr_done',
        lastStt: {
          text: data.text || '',
          language: data.language || '',
          duration: data.duration,
          audio: data.audio || '',
          at: receivedAt,
        },
      }))
      return
    }

    // 后端统一广播：type=conversation, role=user/assistant
    if (type === 'conversation') {
      const role = data.role
      const text = data.text || ''
      const ts = data.timestamp || new Date().toLocaleTimeString()

      if (role === 'user') {
        resetStream()
        addMessage({
          type: 'user',
          text,
          timestamp: ts,
          source: data.source || 'voice',
        })
        return
      }

      if (role === 'assistant') {
        const streamState = data?.meta?.stream_state
        const provider = data?.meta?.provider || data.source || 'AI'
        if (streamState === 'start') {
          streamTextRef.current = ''
          setStreamText('')
          setIsTyping(true)
          setPipeline((prev) => ({ ...prev, phase: 'llm_stream' }))
          return
        }
        if (streamState === 'delta') {
          streamTextRef.current += text
          setStreamText(streamTextRef.current)
          setIsTyping(true)
          setPipeline((prev) => ({ ...prev, phase: 'llm_stream' }))
          return
        }
        if (streamState === 'stop' || streamState === 'error') {
          const finalText = (text || streamTextRef.current || '').trim()
          if (finalText) {
            addMessage({
              type: 'assistant',
              text: finalText,
              timestamp: ts,
              provider,
            })
          }
          setPipeline((prev) => ({
            ...prev,
            phase: prev.lastTts?.state && prev.lastTts?.state !== 'stop' ? 'speaking' : 'idle',
            lastAssistantProvider: provider,
            lastAssistantAt: receivedAt,
          }))
          resetStream()
          return
        }

        resetStream()
        addMessage({
          type: 'assistant',
          text,
          timestamp: ts,
          provider,
        })
        setPipeline((prev) => ({
          ...prev,
          phase: 'idle',
          lastAssistantProvider: provider,
          lastAssistantAt: receivedAt,
        }))
        return
      }
      return
    }

    if (type === 'status') {
      if (data.state === 'thinking') {
        setIsTyping(true)
        setPipeline((prev) => ({
          ...prev,
          phase: 'thinking',
          lastStatus: { state: data.state, text: data.text || '', at: receivedAt },
        }))
      } else if (data.state === 'speaking') {
        setIsTyping(false)
        setPipeline((prev) => ({
          ...prev,
          phase: 'speaking',
          lastStatus: { state: data.state, text: data.text || '', at: receivedAt },
        }))
      }
      return
    }

    if (type === 'tts') {
      const state = String(data.state || '')
      setPipeline((prev) => ({
        ...prev,
        phase: state === 'stop' ? 'idle' : 'speaking',
        lastTts: { state, text: data.text || '', at: receivedAt },
      }))
      return
    }

    // 情绪广播（来自后端 overrides）: {type:'llm', text:'<emotion>', emotion:'happy', ...}
    if (type === 'llm') {
      if (data.emotion) {
        const e = normalizeEmotion(data.emotion)
        setEmotion(e)
        setEmotionEmoji(prev => (data.text ? data.text : (prev || emojiFromEmotion(e))))
        setPipeline((prev) => ({ ...prev, lastEmotionAt: receivedAt }))
      } else if (data.text) {
        setEmotionEmoji(data.text)
        setPipeline((prev) => ({ ...prev, lastEmotionAt: receivedAt }))
      }
      return
    }

    if (type === 'event') {
      addMessage({
        type: 'event',
        text: `${data.event}: ${data.detail || ''}`
      })
      return
    }

    if (type === 'error') {
      const msg = String(data.error || 'unknown error')
      const now = Date.now()
      if (lastErrorRef.current.msg !== msg || now - lastErrorRef.current.ts > 3000) {
        lastErrorRef.current = { msg, ts: now }
        addMessage({ type: 'system', text: tx({ zh: '错误: {msg}', en: 'Error: {msg}' }, { msg }) })
      }
      if (msg === 'device offline') {
        setDeviceOnline(false)
      }
    }
  }


  return { connect }
}
